import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

type ConfirmButtonType = "primary" | "warning";

type PlaybookConfirmationModalProps = {
    isOpen: boolean;
    title: string;
    description: ReactNode;
    confirmLabel: string;
    confirmButtonType?: ConfirmButtonType;
    isLoading?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    dataAutomation: string;
};

const PLAYBOOK_CONFIRMATION_MODAL_STYLES = {
    content: {
        width: `420px`,
        padding: "0",
    },
};

export const PlaybookConfirmationModal = ({
    isOpen,
    title,
    description,
    confirmLabel,
    confirmButtonType = "primary",
    isLoading = false,
    onCancel,
    onConfirm,
    dataAutomation,
}: PlaybookConfirmationModalProps) => {
    const { translate } = useTranslate();

    const handleRequestClose = () => {
        if (isLoading) {
            return;
        }
        onCancel();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleRequestClose}
            customStyles={PLAYBOOK_CONFIRMATION_MODAL_STYLES}
            data-automation={dataAutomation}
        >
            <Modal.Header>
                <Modal.Title data-automation={`${dataAutomation}-title`}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Content>
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["400"]}
                    data-automation={`${dataAutomation}-description`}
                >
                    {description}
                </Text>
            </Modal.Content>
            <Modal.Footer>
                <Button
                    type={confirmButtonType === "warning" ? "flatDark" : "flat"}
                    onClick={onCancel}
                    isDisabled={isLoading}
                    dataAutomation={`${dataAutomation}-cancel`}
                >
                    {translate("si.common.button.cancel")}
                </Button>
                <Button
                    type={confirmButtonType}
                    onClick={onConfirm}
                    isDisabled={isLoading}
                    isLoading={isLoading}
                    dataAutomation={`${dataAutomation}-confirm`}
                >
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
